import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  socketClient,
  CollaborationMessage,
  CursorMessage,
  GlobalPresenceData,
  OperationAckData,
  PresenceMessage,
  ProjectStateData,
  SelectionMessage,
  UserJoinedData,
  UserLeftData,
} from "./socketClient";
import { config } from "@/config";
import { useAuthStore } from "@/stores/auth/auth-store";
import { logger } from "@/utils";
import { initAutomationRefreshListener } from "@/stores/data/refresh-store";
import { Operation } from "@/types";
import type { CursorPosition } from "./socketClient";

interface WebSocketEventDataMap {
  projectStateReceived: ProjectStateData;
  userJoined: UserJoinedData;
  userLeft: UserLeftData;
  operationReceived: CollaborationMessage;
  operationAck: OperationAckData;
  cursorUpdate: CursorMessage;
  selectionUpdate: SelectionMessage;
  presenceUpdate: PresenceMessage;
  globalPresence: GlobalPresenceData;
}

type WebSocketEventName = keyof WebSocketEventDataMap;
type WebSocketEventHandler<K extends WebSocketEventName> = (
  data: WebSocketEventDataMap[K],
) => void;
type WebSocketHandlerSetMap = {
  [K in WebSocketEventName]: Set<WebSocketEventHandler<K>>;
};

function toError(error: unknown, fallback: string): Error {
  return error instanceof Error ? error : new Error(fallback);
}

function isAuthenticationSocketError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.trim().toLowerCase();
  return (
    message.includes("authentication failed") ||
    message.includes("unauthorized") ||
    message.includes("invalid token") ||
    message.includes("jwt")
  );
}

const createWebSocketHandlerSets = (): WebSocketHandlerSetMap => ({
  projectStateReceived: new Set(),
  userJoined: new Set(),
  userLeft: new Set(),
  operationReceived: new Set(),
  operationAck: new Set(),
  cursorUpdate: new Set(),
  selectionUpdate: new Set(),
  presenceUpdate: new Set(),
  globalPresence: new Set(),
});

interface WebSocketContextValue {
  // Connection state
  isConnected: boolean;
  connectionState: string;
  isReconnecting: boolean;
  reconnectionAttempts: number;

  // Project state
  isInProject: boolean;
  currentProjectId: string | null;

  // Connection methods
  connect: () => Promise<void>;
  disconnect: () => void;

  // Project methods
  joinProject: (projectId: string) => Promise<void>;
  leaveProject: () => Promise<void>;

  // Collaboration methods
  sendOperation: (operation: Operation) => void;
  sendCursorUpdate: (position: CursorPosition, throttle?: boolean) => void;
  sendSelectionUpdate: (selection: string[]) => void;
  sendPresenceUpdate: (action: string) => void;
  requestSync: () => void;

  // Event handlers
  onProjectStateReceived: (
    handler: (data: ProjectStateData) => void,
  ) => () => void;
  onUserJoined: (handler: (data: UserJoinedData) => void) => () => void;
  onUserLeft: (handler: (data: UserLeftData) => void) => () => void;
  onOperationReceived: (
    handler: (data: CollaborationMessage) => void,
  ) => () => void;
  onOperationAck: (handler: (data: OperationAckData) => void) => () => void;
  onCursorUpdate: (handler: (data: CursorMessage) => void) => () => void;
  onSelectionUpdate: (handler: (data: SelectionMessage) => void) => () => void;
  onPresenceUpdate: (handler: (data: PresenceMessage) => void) => () => void;
  onGlobalPresence: (handler: (data: GlobalPresenceData) => void) => () => void;

  // Error handling
  lastError: Error | null;
  clearError: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
  autoConnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
}

interface WebSocketRuntimeState {
  isConnected: boolean;
  connectionState: string;
  isReconnecting: boolean;
  reconnectionAttempts: number;
  isInProject: boolean;
  currentProjectId: string | null;
  lastError: Error | null;
  shouldConnect: boolean;
}

let webSocketProviderInstanceCount = 0;

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  autoConnect = true,
  maxReconnectAttempts = 5,
  reconnectDelay = 1000,
}) => {
  const { user, tokens, isAuthenticated } = useAuthStore();
  const ownerIdRef = useRef(
    `websocket-provider-${++webSocketProviderInstanceCount}`,
  );
  const blockedAuthTokenRef = useRef<string | null>(null);

  const [socketState, setSocketState] = useState<WebSocketRuntimeState>({
    isConnected: false,
    connectionState: "disconnected",
    isReconnecting: false,
    reconnectionAttempts: 0,
    isInProject: false,
    currentProjectId: null,
    lastError: null,
    shouldConnect: autoConnect,
  });
  const {
    isConnected,
    connectionState,
    isReconnecting,
    reconnectionAttempts,
    isInProject,
    currentProjectId,
    lastError,
    shouldConnect,
  } = socketState;

  // Event handlers storage
  const eventHandlersRef = useRef<WebSocketHandlerSetMap>(
    createWebSocketHandlerSets(),
  );

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(async (): Promise<void> => {
    if (!user || !tokens?.accessToken) {
      throw new Error("User authentication required");
    }

    if (
      socketClient.isConnected() &&
      socketClient.getAuthToken() === tokens.accessToken
    ) {
      logger.warn("Already connected to WebSocket");
      return;
    }

    try {
      setSocketState((prev) => ({
        ...prev,
        lastError: null,
        connectionState: "connecting",
      }));

      await socketClient.acquireConnection(ownerIdRef.current, {
        url: config.websocket.url,
        token: tokens.accessToken,
        userId: user.id,
        reconnectionAttempts: maxReconnectAttempts,
        reconnectionDelay: reconnectDelay,
      });

      setSocketState((prev) => ({ ...prev, shouldConnect: true }));
      blockedAuthTokenRef.current = null;
    } catch (error: unknown) {
      if (isAuthenticationSocketError(error)) {
        blockedAuthTokenRef.current = tokens.accessToken;
      }
      setSocketState((prev) => ({
        ...prev,
        lastError: toError(error, "Failed to connect to WebSocket"),
        connectionState: "disconnected",
      }));
      throw error;
    }
  }, [user, tokens, maxReconnectAttempts, reconnectDelay]);

  /**
   * Disconnect socket on logout — prevents stale connection from leaking
   * user data to the next login session
   */
  useEffect(() => {
    if (!isAuthenticated) {
      logger.info("User logged out, releasing WebSocket ownership");
      blockedAuthTokenRef.current = null;
      socketClient.releaseConnection(ownerIdRef.current);
      setSocketState((prev) => ({
        ...prev,
        shouldConnect: false,
        isConnected: false,
        connectionState: "disconnected",
        isInProject: false,
        currentProjectId: null,
        isReconnecting: false,
        reconnectionAttempts: 0,
      }));
    }
  }, [isAuthenticated]);

  /**
   * Detect token changes and reconnect with new token
   * This prevents the socket from trying to reconnect with an expired token
   */
  useEffect(() => {
    if (!shouldConnect || !user || !tokens?.accessToken) {
      return;
    }

    if (
      socketClient.isConnected() &&
      socketClient.getAuthToken() === tokens.accessToken
    ) {
      return;
    }

    connect().catch((error) => {
      logger.error("Token-aware auto-connect failed:", error);
    });
  }, [connect, shouldConnect, tokens?.accessToken, user]);

  /**
   * Cleanup on real unmount (not StrictMode remounts)
   */
  useEffect(() => {
    const ownerId = ownerIdRef.current;
    // Track if this is a real unmount vs StrictMode remount
    let isRealUnmount = false;

    const timeoutId = setTimeout(() => {
      isRealUnmount = true;
    }, 100); // After 100ms, consider it a real mount

    return () => {
      if (!isRealUnmount) {
        clearTimeout(timeoutId);
        logger.debug("WebSocketProvider cleanup skipped for StrictMode remount");
        return;
      }

      setTimeout(() => {
        logger.debug("WebSocketProvider cleanup releasing socket ownership");
        socketClient.releaseConnection(ownerId);
      }, 100);
    };
  }, []);

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    socketClient.releaseConnection(ownerIdRef.current);
    setSocketState((prev) => ({
      ...prev,
      shouldConnect: false,
      isConnected: false,
      connectionState: "disconnected",
      isInProject: false,
      currentProjectId: null,
      isReconnecting: false,
      reconnectionAttempts: 0,
    }));
  }, []);

  /**
   * Join project room
   */
  const joinProject = useCallback(async (projectId: string): Promise<void> => {
    if (!socketClient.isConnected()) {
      throw new Error("Not connected to WebSocket server");
    }

    try {
      await socketClient.joinProject(projectId);
    } catch (error: unknown) {
      logger.error("Failed to join project:", error);
      setSocketState((prev) => ({
        ...prev,
        lastError: toError(error, "Failed to join project"),
      }));
      throw error;
    }
  }, []);

  /**
   * Leave project room
   */
  const leaveProject = useCallback(async (): Promise<void> => {
    try {
      await socketClient.leaveProject();
    } catch (error: unknown) {
      logger.error("Failed to leave project:", error);
      setSocketState((prev) => ({
        ...prev,
        lastError: toError(error, "Failed to leave project"),
      }));
      throw error;
    }
  }, []);

  /**
   * Send operation
   */
  const sendOperation = useCallback((operation: Operation) => {
    try {
      socketClient.sendOperation(operation);
    } catch (error: unknown) {
      logger.error("Failed to send operation:", error);
      setSocketState((prev) => ({
        ...prev,
        lastError: toError(error, "Failed to send operation"),
      }));
      throw error;
    }
  }, []);

  /**
   * Send cursor update
   */
  const sendCursorUpdate = useCallback(
    (position: CursorPosition, throttle = true) => {
      try {
        socketClient.sendCursorUpdate(position, throttle);
      } catch (error: unknown) {
        logger.error("Failed to send cursor update:", error);
        // Don't set error for cursor updates as they're frequent
      }
    },
    [],
  );

  /**
   * Send selection update
   */
  const sendSelectionUpdate = useCallback((selection: string[]) => {
    try {
      socketClient.sendSelectionUpdate(selection);
    } catch (error: unknown) {
      logger.error("Failed to send selection update:", error);
      // Don't set error for selection updates
    }
  }, []);

  /**
   * Send presence update
   */
  const sendPresenceUpdate = useCallback((action: string) => {
    try {
      socketClient.sendPresenceUpdate(action);
    } catch (error: unknown) {
      logger.error("Failed to send presence update:", error);
      // Don't set error for presence updates
    }
  }, []);

  /**
   * Request sync
   */
  const requestSync = useCallback(() => {
    try {
      socketClient.requestSync();
    } catch (error: unknown) {
      logger.error("Failed to request sync:", error);
      setSocketState((prev) => ({
        ...prev,
        lastError: toError(error, "Failed to request sync"),
      }));
      throw error;
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setSocketState((prev) => ({ ...prev, lastError: null }));
  }, []);

  /**
   * Emit to registered handlers
   */
  const emitToHandlers = useCallback(
    <K extends WebSocketEventName>(
      eventName: K,
      data: WebSocketEventDataMap[K],
    ) => {
      const handlers = eventHandlersRef.current[eventName];
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(data);
          } catch (error) {
            logger.error(`Error in event handler for ${eventName}:`, error);
          }
        });
      }
    },
    [],
  );

  /**
   * Register event handler
   */
  const registerEventHandler = useCallback(
    <K extends WebSocketEventName>(
      eventName: K,
      handler: WebSocketEventHandler<K>,
    ) => {
      const handlers = eventHandlersRef.current[eventName];
      handlers.add(handler);

      // Return cleanup function
      return () => {
        handlers.delete(handler);
      };
    },
    [],
  );

  // Event handler registration functions
  const onProjectStateReceived = useCallback(
    (handler: (data: ProjectStateData) => void) =>
      registerEventHandler("projectStateReceived", handler),
    [registerEventHandler],
  );

  const onUserJoined = useCallback(
    (handler: (data: UserJoinedData) => void) =>
      registerEventHandler("userJoined", handler),
    [registerEventHandler],
  );

  const onUserLeft = useCallback(
    (handler: (data: UserLeftData) => void) =>
      registerEventHandler("userLeft", handler),
    [registerEventHandler],
  );

  const onOperationReceived = useCallback(
    (handler: (data: CollaborationMessage) => void) =>
      registerEventHandler("operationReceived", handler),
    [registerEventHandler],
  );

  const onOperationAck = useCallback(
    (handler: (data: OperationAckData) => void) =>
      registerEventHandler("operationAck", handler),
    [registerEventHandler],
  );

  const onCursorUpdate = useCallback(
    (handler: (data: CursorMessage) => void) =>
      registerEventHandler("cursorUpdate", handler),
    [registerEventHandler],
  );

  const onSelectionUpdate = useCallback(
    (handler: (data: SelectionMessage) => void) =>
      registerEventHandler("selectionUpdate", handler),
    [registerEventHandler],
  );

  const onPresenceUpdate = useCallback(
    (handler: (data: PresenceMessage) => void) =>
      registerEventHandler("presenceUpdate", handler),
    [registerEventHandler],
  );

  const onGlobalPresence = useCallback(
    (handler: (data: GlobalPresenceData) => void) =>
      registerEventHandler("globalPresence", handler),
    [registerEventHandler],
  );

  /**
   * Initialize socket event listeners
   */
  useEffect(() => {
    if (!socketClient) return;

    // Connection events
    const handleConnected = () => {
      blockedAuthTokenRef.current = null;
      setSocketState((prev) => ({
        ...prev,
        isConnected: true,
        connectionState: "connected",
        isReconnecting: false,
        reconnectionAttempts: 0,
        lastError: null,
      }));
      logger.info("WebSocket connected successfully");
      // Wire automation rule refresh bridge (idempotent, safe to call multiple times)
      initAutomationRefreshListener();
    };

    const handleDisconnected = (reason: string) => {
      setSocketState((prev) => ({
        ...prev,
        isConnected: false,
        connectionState: "disconnected",
        isInProject: false,
        currentProjectId: null,
        shouldConnect:
          reason === "io server disconnect" ? false : prev.shouldConnect,
      }));
      logger.warn("WebSocket disconnected:", reason);
    };

    const handleReconnecting = (attemptNumber: number) => {
      setSocketState((prev) => ({
        ...prev,
        isReconnecting: true,
        connectionState: "reconnecting",
        reconnectionAttempts: attemptNumber,
      }));
      logger.info(`WebSocket reconnecting, attempt ${attemptNumber}`);
    };

    const handleReconnected = () => {
      setSocketState((prev) => ({
        ...prev,
        isConnected: true,
        connectionState: "connected",
        isReconnecting: false,
        reconnectionAttempts: 0,
        lastError: null,
      }));
      logger.info("WebSocket reconnected successfully");

      // Rejoin project if we were in one
      if (currentProjectId) {
        joinProject(currentProjectId).catch((error) => {
          logger.error("Failed to rejoin project after reconnection:", error);
        });
      }
    };

    const handleError = (error: Error) => {
      setSocketState((prev) => ({ ...prev, lastError: error }));
      logger.error("WebSocket error:", error);
    };

    const handleProjectJoined = (data: ProjectStateData) => {
      setSocketState((prev) => ({
        ...prev,
        isInProject: true,
        currentProjectId: socketClient.getCurrentProjectId(),
      }));
      emitToHandlers("projectStateReceived", data);
    };

    const handleProjectLeft = () => {
      setSocketState((prev) => ({
        ...prev,
        isInProject: false,
        currentProjectId: null,
      }));
    };

    const handleProjectStateReceived = (data: ProjectStateData) =>
      emitToHandlers("projectStateReceived", data);
    const handleUserJoined = (data: UserJoinedData) =>
      emitToHandlers("userJoined", data);
    const handleUserLeft = (data: UserLeftData) =>
      emitToHandlers("userLeft", data);
    const handleOperationReceived = (data: CollaborationMessage) =>
      emitToHandlers("operationReceived", data);
    const handleOperationAck = (data: OperationAckData) =>
      emitToHandlers("operationAck", data);
    const handleCursorUpdate = (data: CursorMessage) =>
      emitToHandlers("cursorUpdate", data);
    const handleSelectionUpdate = (data: SelectionMessage) =>
      emitToHandlers("selectionUpdate", data);
    const handlePresenceUpdate = (data: PresenceMessage) =>
      emitToHandlers("presenceUpdate", data);
    const handleGlobalPresence = (data: GlobalPresenceData) =>
      emitToHandlers("globalPresence", data);

    // Register all event handlers
    const unsubscribers = [
      socketClient.on("connected", handleConnected),
      socketClient.on("disconnected", handleDisconnected),
      socketClient.on("reconnecting", handleReconnecting),
      socketClient.on("reconnected", handleReconnected),
      socketClient.on("error", handleError),
      socketClient.on("projectJoined", handleProjectJoined),
      socketClient.on("projectLeft", handleProjectLeft),
      socketClient.on("projectStateReceived", handleProjectStateReceived),
      socketClient.on("userJoined", handleUserJoined),
      socketClient.on("userLeft", handleUserLeft),
      socketClient.on("operationReceived", handleOperationReceived),
      socketClient.on("operationAck", handleOperationAck),
      socketClient.on("cursorUpdate", handleCursorUpdate),
      socketClient.on("selectionUpdate", handleSelectionUpdate),
      socketClient.on("presenceUpdate", handlePresenceUpdate),
      socketClient.on("globalPresence", handleGlobalPresence),
    ];

    return () => {
      // Cleanup event handlers
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [currentProjectId, emitToHandlers, joinProject]);

  /**
   * Auto-connect when user and tokens are available
   */
  useEffect(() => {
    if (
      shouldConnect &&
      user &&
      tokens &&
      blockedAuthTokenRef.current !== tokens.accessToken &&
      (
        !socketClient.isConnected() ||
        socketClient.getAuthToken() !== tokens.accessToken ||
        connectionState === "disconnected"
      )
    ) {
      connect().catch((error) => {
        logger.error("Auto-connect failed:", error);
        if (isAuthenticationSocketError(error)) {
          blockedAuthTokenRef.current = tokens.accessToken;
        }
        // Don't keep retrying if connection consistently fails
        const hasReachedMaxAttempts =
          reconnectionAttempts >= maxReconnectAttempts;
        setSocketState((prev) => ({
          ...prev,
          lastError: error,
          shouldConnect: hasReachedMaxAttempts ? false : prev.shouldConnect,
        }));
        if (hasReachedMaxAttempts) {
          logger.warn(
            "Max auto-connect attempts reached, disabling auto-connect",
          );
        }
      });
    }
  }, [
    connect,
    connectionState,
    maxReconnectAttempts,
    reconnectionAttempts,
    shouldConnect,
    tokens,
    user,
  ]);

  const contextValue: WebSocketContextValue = {
    // Connection state
    isConnected,
    connectionState,
    isReconnecting,
    reconnectionAttempts,

    // Project state
    isInProject,
    currentProjectId,

    // Connection methods
    connect,
    disconnect,

    // Project methods
    joinProject,
    leaveProject,

    // Collaboration methods
    sendOperation,
    sendCursorUpdate,
    sendSelectionUpdate,
    sendPresenceUpdate,
    requestSync,

    // Event handlers
    onProjectStateReceived,
    onUserJoined,
    onUserLeft,
    onOperationReceived,
    onOperationAck,
    onCursorUpdate,
    onSelectionUpdate,
    onPresenceUpdate,
    onGlobalPresence,

    // Error handling
    lastError,
    clearError,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Connection status component for debugging
export const WebSocketStatus: React.FC = () => {
  const {
    isConnected,
    connectionState,
    isReconnecting,
    reconnectionAttempts,
    isInProject,
    currentProjectId,
    lastError,
  } = useWebSocket();

  if (process.env.NODE_ENV === "production") {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 bg-black bg-opacity-75 text-white p-2 rounded text-xs font-mono">
      <div>WebSocket: {connectionState}</div>
      <div>Connected: {isConnected ? "✓" : "✗"}</div>
      {isReconnecting && (
        <div>Reconnecting... (attempt {reconnectionAttempts})</div>
      )}
      <div>In Project: {isInProject ? "✓" : "✗"}</div>
      {currentProjectId && <div>Project: {currentProjectId.slice(-8)}</div>}
      {lastError && (
        <div className="text-red-400">Error: {lastError.message}</div>
      )}
    </div>
  );
};
